import { useState, useCallback, useRef, useEffect } from 'react';
import { runCode, isTauri } from '@/lib/runtime-bridge';
import { ControlDefinition } from '@/types/api';

interface SiteInfo {
    id: string;
    feature: { usePyodide: boolean };
}

interface ExecutionEngineOptions {
    site: SiteInfo;
    editorCode: string;
    controlValues: Record<string, any>;
    controls: ControlDefinition[];
    currentSketchId: string;
    runtimeReady: boolean;
    initialData: any;
}

export interface ExecutionEngineState {
    isExecuting: boolean;
    executionStatus: string;
    plotData: any;
    plotLayout: any;
    gcode: string;
    steps: any[];
    fcLoaded: boolean;
    stepsCount: number | undefined;
    svgThumbnail: string | null;
    executionErrors: string | null;
    lastExecutionTime: number;
    avgExecutionTime: number;
    autoCompile: boolean;
    autoUpdate: boolean;
}

export interface ExecutionEngineActions {
    executeCode: (silent?: boolean) => Promise<void>;
    setAutoCompile: (enabled: boolean) => void;
    setAutoUpdate: (enabled: boolean) => void;
    setSvgThumbnail: (thumbnail: string | null) => void;
    setExecutionStatus: (status: string) => void;
    setExecutionErrors: (errors: string | null) => void;
}

export function useExecutionEngine({
    site,
    editorCode,
    controlValues,
    controls,
    currentSketchId,
    runtimeReady,
    initialData,
}: ExecutionEngineOptions): [ExecutionEngineState, ExecutionEngineActions] {
    const [isExecuting, setIsExecuting] = useState(false);
    const [executionStatus, setExecutionStatus] = useState('Ready');
    const [plotData, setPlotData] = useState<any>(null);
    const [plotLayout, setPlotLayout] = useState<any>(null);
    const [gcode, setGcode] = useState('');
    const [steps, setSteps] = useState<any[]>([]);
    const [fcLoaded, setFcLoaded] = useState(false);
    const [stepsCount, setStepsCount] = useState<number | undefined>(undefined);
    const [svgThumbnail, setSvgThumbnail] = useState<string | null>(null);
    const [executionErrors, setExecutionErrors] = useState<string | null>(null);
    const [lastExecutionTime, setLastExecutionTime] = useState(0);
    const [avgExecutionTime, setAvgExecutionTime] = useState(0);
    const [autoCompile, setAutoCompile] = useState(false);
    const [autoUpdate, setAutoUpdate] = useState(true);

    const executionTimesRef = useRef<number[]>([]);
    const controlUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);
    const executeCodeRef = useRef<((silent?: boolean) => Promise<void>) | null>(null);
    const lastControlValuesRef = useRef<string>(JSON.stringify({}));
    const lastControlCountRef = useRef<number>(controls.length);
    const hasAutoRun = useRef(false);
    const lastRenderedCode = useRef('');
    const autoCompileTimerRef = useRef<NodeJS.Timeout | null>(null);

    const executeCode = useCallback(async (silent: boolean = false) => {
        if (!runtimeReady) return;

        hasAutoRun.current = true;
        const startTime = performance.now();

        if (!silent) {
            setIsExecuting(true);
            setExecutionStatus('Executing...');
            setPlotData(null);
            setGcode('');
            setSteps([]);
        }

        lastRenderedCode.current = editorCode;

        try {
            let modifiedCode = editorCode;

            // Handle file uploads for Pyodide
            if (site.id === 'py' && runtimeReady) {
                try {
                    const { getPyodide } = await import('@/lib/services/pyodideService');
                    const pyodide = await getPyodide();

                    for (const [, value] of Object.entries(controlValues)) {
                        if (value instanceof File) {
                            try {
                                const arrayBuffer = await value.arrayBuffer();
                                const uint8Array = new Uint8Array(arrayBuffer);
                                pyodide.FS.writeFile(value.name, uint8Array);
                            } catch (err) {
                                console.error(`Failed to write file ${value.name} to Pyodide FS:`, err);
                            }
                        }
                    }
                } catch (e) {
                    console.error('Error preparing Pyodide files:', e);
                }
            }

            // Inject control values
            if (Object.keys(controlValues).length > 0) {
                if (site.id === 'py' || site.id === 'desktop') {
                    const assignments = Object.entries(controlValues)
                        .map(([name, value]) => {
                            if (value instanceof File) return `${name} = "${value.name}"`;
                            if (typeof value === 'string') return `${name} = "${value}"`;
                            return `${name} = ${value}`;
                        })
                        .join('\n');
                    modifiedCode = `${assignments}\n\n${editorCode}`;
                } else {
                    const assignments = Object.entries(controlValues)
                        .map(([name, value]) => {
                            if (value instanceof File) return `const ${name} = "${value.name}";`;
                            if (typeof value === 'string') return `const ${name} = "${value}";`;
                            return `const ${name} = ${value};`;
                        })
                        .join('\n');
                    modifiedCode = `${assignments}\n\n${editorCode}`;
                }
            }

            // Prepare files for JS runtime
            const files: Record<string, ArrayBuffer> = {};
            for (const [, value] of Object.entries(controlValues)) {
                if (value instanceof File) {
                    try {
                        files[value.name] = await value.arrayBuffer();
                    } catch (err) {
                        console.error(`Failed to read file ${value.name}:`, err);
                    }
                }
            }

            const variant = site.id === 'js' ? 'js' : 'py';
            const result = await runCode(modifiedCode, variant, files, currentSketchId);
            const data: any = result;

            if (data.success && data.result) {
                const endTime = performance.now();
                const execTime = endTime - startTime;

                setExecutionErrors(null);
                setLastExecutionTime(execTime);
                executionTimesRef.current.push(execTime);

                if (executionTimesRef.current.length > 5) {
                    executionTimesRef.current.shift();
                }

                const avg = executionTimesRef.current.reduce((a, b) => a + b, 0) / executionTimesRef.current.length;
                setAvgExecutionTime(avg);

                setExecutionStatus(`Completed in ${execTime.toFixed(0)}ms ${isTauri() ? '(Desktop)' : '(Web)'}`);
                if (typeof data.result.fcLoaded === 'boolean') setFcLoaded(data.result.fcLoaded);
                if (typeof data.result.stepsCount === 'number') setStepsCount(data.result.stepsCount);
                else if (Array.isArray(data.result.steps)) setStepsCount(data.result.steps.length);

                // Promote plot if gcode object previously contained plot (legacy structure)
                if (!data.result.plot && data.result.gcode && typeof data.result.gcode === 'object' && (data.result.gcode as any).plot) {
                    data.result.plot = (data.result.gcode as any).plot;
                }

                if (data.result.plot) {
                    setPlotData(data.result.plot.data);
                    setPlotLayout(data.result.plot.layout);

                    try {
                        const { generateSVGThumbnail } = await import('@/lib/svg-thumbnail');
                        const thumbnail = generateSVGThumbnail(data.result.plot.data, {
                            width: 200,
                            height: 200
                        });
                        setSvgThumbnail(thumbnail);
                    } catch (thumbnailError) {
                        console.warn('Failed to generate thumbnail:', thumbnailError);
                        setSvgThumbnail(null);
                    }
                }

                if (data.result.gcode) {
                    const gVal = data.result.gcode;
                    if (typeof gVal === 'string') {
                        setGcode(gVal);
                    } else if (typeof gVal === 'object' && typeof (gVal as any).gcode === 'string') {
                        setGcode((gVal as any).gcode);
                    } else {
                        setGcode(JSON.stringify(gVal));
                    }
                }

                if (data.result.steps && Array.isArray(data.result.steps)) {
                    setSteps(data.result.steps);
                } else {
                    setSteps([]);
                }
            } else {
                const errorMsg = data.error || 'Unknown error';
                setExecutionStatus(`Error: ${errorMsg}`);
                setExecutionErrors(errorMsg);
            }
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Error executing code';
            console.error('Execution error:', error);
            setExecutionStatus(errorMsg);
            setExecutionErrors(errorMsg);
        } finally {
            if (!silent) {
                setIsExecuting(false);
            }
        }
    }, [editorCode, controlValues, runtimeReady, site, currentSketchId]);

    // Keep executeCodeRef up to date
    useEffect(() => {
        executeCodeRef.current = executeCode;
    }, [executeCode]);

    // Auto-run code when runtime ready and we have initial data
    useEffect(() => {
        if (!hasAutoRun.current && runtimeReady && editorCode.trim() && initialData) {
            executeCode();
        }
    }, [executeCode, runtimeReady, editorCode, initialData]);

    // Auto-compile with debounce when code changes
    useEffect(() => {
        if (!autoCompile || !runtimeReady || isExecuting) return;

        if (autoCompileTimerRef.current) {
            clearTimeout(autoCompileTimerRef.current);
        }

        if (editorCode.trim() && editorCode !== lastRenderedCode.current) {
            autoCompileTimerRef.current = setTimeout(() => {
                executeCode();
            }, 500);
        }

        return () => {
            if (autoCompileTimerRef.current) {
                clearTimeout(autoCompileTimerRef.current);
            }
        };
    }, [editorCode, autoCompile, runtimeReady, isExecuting, executeCode]);

    // Intelligent auto-update when control values change
    useEffect(() => {
        if (!autoUpdate) return;
        if (!runtimeReady || isExecuting || controls.length === 0) return;
        if (!hasAutoRun.current) return;

        const currentControlValuesStr = JSON.stringify(controlValues);
        if (currentControlValuesStr === lastControlValuesRef.current) return;

        lastControlValuesRef.current = currentControlValuesStr;

        if (controlUpdateTimerRef.current) {
            clearTimeout(controlUpdateTimerRef.current);
        }

        const DELAY_FACTOR = 2;
        const MIN_DELAY = 30;
        const MAX_DELAY = 1000;

        let debounceDelay = MIN_DELAY;
        if (avgExecutionTime > 0) {
            debounceDelay = Math.max(MIN_DELAY, Math.min(MAX_DELAY, avgExecutionTime * DELAY_FACTOR));
        }

        controlUpdateTimerRef.current = setTimeout(() => {
            if (executeCodeRef.current) {
                executeCodeRef.current(true);
            }
        }, debounceDelay);

        return () => {
            if (controlUpdateTimerRef.current) {
                clearTimeout(controlUpdateTimerRef.current);
            }
        };
    }, [controlValues, runtimeReady, isExecuting, avgExecutionTime, controls.length, autoUpdate]);

    // Trigger update when a new control is added
    useEffect(() => {
        if (!autoUpdate || !runtimeReady || !hasAutoRun.current) return;

        const currentControlCount = controls.length;
        const previousControlCount = lastControlCountRef.current;

        if (currentControlCount > previousControlCount) {
            lastControlCountRef.current = currentControlCount;
            setTimeout(() => {
                if (executeCodeRef.current) {
                    executeCodeRef.current(true);
                }
            }, 100);
        } else {
            lastControlCountRef.current = currentControlCount;
        }
    }, [controls.length, runtimeReady, autoUpdate]);

    const state: ExecutionEngineState = {
        isExecuting,
        executionStatus,
        plotData,
        plotLayout,
        gcode,
        steps,
        fcLoaded,
        stepsCount,
        svgThumbnail,
        executionErrors,
        lastExecutionTime,
        avgExecutionTime,
        autoCompile,
        autoUpdate,
    };

    const actions: ExecutionEngineActions = {
        executeCode,
        setAutoCompile,
        setAutoUpdate,
        setSvgThumbnail,
        setExecutionStatus,
        setExecutionErrors,
    };

    return [state, actions];
}
